import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, InputNumber, Popconfirm, Space, Spin, Table, Typography, message,
} from 'antd';
import { DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import axios from 'axios';
import API from '../config/api';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { useLanguage } from './LanguageContext';
import { useOwnerContext } from './OwnerContext';

const { Text } = Typography;

interface AttendanceZone {
  _id: string;
  doctor_id: string;
  clinic_id: string;
  clinic_name: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  enabled: boolean;
}

interface Props {
  /** Undefined shows every linked clinic; a value shows one clinic only. */
  clinicId?: string | null;
}

const ClinicAttendanceZoneSettings: React.FC<Props> = ({ clinicId }) => {
  const { t } = useLanguage();
  const { doctorId } = useDoctorContext();
  const { clinics, loading: clinicsLoading } = useClinicContext();
  const { canManageOwnerFeatures, loading: ownerLoading } = useOwnerContext();
  const [zonesByClinic, setZonesByClinic] = useState<Record<string, AttendanceZone>>({});
  const [ownerByClinic, setOwnerByClinic] = useState<Record<string, string>>({});
  const [radiusByClinic, setRadiusByClinic] = useState<Record<string, number>>({});
  const [zonesLoading, setZonesLoading] = useState(false);
  const [savingClinicId, setSavingClinicId] = useState<string | null>(null);

  const visibleClinics = useMemo(
    () => clinicId === undefined ? clinics : clinics.filter((clinic) => clinic._id === clinicId),
    [clinicId, clinics]
  );

  const clinicDisplayName = (clinic: { name?: string; _id: string }) => clinic.name || clinic._id;

  const fetchZones = useCallback(async () => {
    if (!doctorId || visibleClinics.length === 0) {
      setZonesByClinic({});
      return;
    }

    setZonesLoading(true);
    try {
      const ownerEntries = await Promise.all(
        visibleClinics.map(async (clinic) => [
          clinic._id,
          await getClinicOwnerDoctorId(clinic._id, doctorId) || doctorId,
        ] as const)
      );
      const nextOwnerByClinic = Object.fromEntries(ownerEntries);
      const responses = await Promise.all(
        ownerEntries.map(([targetClinicId, ownerId]) => axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_ZONES(ownerId)}`,
          { params: { clinic_id: targetClinicId } }
        ))
      );
      const zones = responses.flatMap((response) => response.data?.zones || []) as AttendanceZone[];
      const nextZonesByClinic: Record<string, AttendanceZone> = {};

      visibleClinics.forEach((clinic) => {
        const ownerId = nextOwnerByClinic[clinic._id];
        const zone = zones.find((item) =>
          item.clinic_id === clinic._id && item.doctor_id === ownerId && item.enabled !== false
        ) || zones.find((item) => item.clinic_id === clinic._id && item.enabled !== false);
        if (zone) nextZonesByClinic[clinic._id] = zone;
      });

      setOwnerByClinic(nextOwnerByClinic);
      setZonesByClinic(nextZonesByClinic);
      setRadiusByClinic((previous) => {
        const next = { ...previous };
        visibleClinics.forEach((clinic) => {
          const zone = nextZonesByClinic[clinic._id];
          if (zone) next[clinic._id] = zone.radius_meters;
          else if (next[clinic._id] == null) next[clinic._id] = 150;
        });
        return next;
      });
    } catch {
      message.error(t('error'));
    } finally {
      setZonesLoading(false);
    }
  }, [doctorId, t, visibleClinics]);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const captureLocation = (targetClinicId: string) => {
    if (!canManageOwnerFeatures || !doctorId) return;
    if (!navigator.geolocation) {
      message.error(t('attendanceGeoNotSupported'));
      return;
    }

    setSavingClinicId(targetClinicId);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const clinic = visibleClinics.find((item) => item._id === targetClinicId);
          const ownerId = ownerByClinic[targetClinicId]
            || await getClinicOwnerDoctorId(targetClinicId, doctorId)
            || doctorId;
          const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_ZONE}`, {
            doctor_id: ownerId,
            clinic_id: targetClinicId,
            clinic_name: clinic ? clinicDisplayName(clinic) : '',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            radius_meters: radiusByClinic[targetClinicId] ?? 150,
            enabled: true,
          });
          if (response.data?.success) {
            message.success(t('attendanceZoneSaved'));
            await fetchZones();
          }
        } catch {
          message.error(t('error'));
        } finally {
          setSavingClinicId(null);
        }
      },
      (error) => {
        setSavingClinicId(null);
        const errorMessage = error.code === 1
          ? t('attendanceGeoPermissionDenied')
          : error.code === 2
            ? t('attendanceGeoUnavailable')
            : error.code === 3
              ? t('attendanceGeoTimeout')
              : t('attendanceGeoDenied');
        message.error(errorMessage);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const deleteZone = async (targetClinicId: string) => {
    if (!canManageOwnerFeatures || !doctorId) return;
    const ownerId = ownerByClinic[targetClinicId]
      || await getClinicOwnerDoctorId(targetClinicId, doctorId)
      || doctorId;
    try {
      await axios.delete(`${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_ZONE_DELETE(ownerId, targetClinicId)}`);
      message.success(t('attendanceZoneDeleted'));
      await fetchZones();
    } catch {
      message.error(t('error'));
    }
  };

  const columns = [
    {
      title: t('attendanceClinic'),
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, clinic: { _id: string; name: string }) => clinicDisplayName(clinic),
    },
    {
      title: t('attendanceRadiusM'),
      key: 'radius',
      render: (_: unknown, clinic: { _id: string }) => canManageOwnerFeatures ? (
        <InputNumber
          min={30}
          max={2000}
          value={radiusByClinic[clinic._id] ?? zonesByClinic[clinic._id]?.radius_meters ?? 150}
          onChange={(value) => setRadiusByClinic((previous) => ({
            ...previous,
            [clinic._id]: Number(value) || 150,
          }))}
          style={{ width: 110 }}
        />
      ) : <Text>{zonesByClinic[clinic._id]?.radius_meters ?? '—'}</Text>,
    },
    {
      title: t('attendanceZoneStatus'),
      key: 'status',
      render: (_: unknown, clinic: { _id: string }) => {
        const zone = zonesByClinic[clinic._id];
        if (!zone) return <Text type="secondary">{t('attendanceNoZone')}</Text>;
        return <Text dir="ltr">{zone.latitude.toFixed(5)}, {zone.longitude.toFixed(5)} · {zone.radius_meters} m</Text>;
      },
    },
    ...(canManageOwnerFeatures ? [{
      title: t('actions'),
      key: 'actions',
      render: (_: unknown, clinic: { _id: string }) => (
        <Space wrap>
          <Button
            type="primary"
            icon={<EnvironmentOutlined />}
            loading={savingClinicId === clinic._id}
            onClick={() => captureLocation(clinic._id)}
          >
            {t('attendanceUseDeviceLocation')}
          </Button>
          {zonesByClinic[clinic._id] && (
            <Popconfirm
              title={t('attendanceConfirmDeleteZone')}
              onConfirm={() => deleteZone(clinic._id)}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <Card title={t('attendanceClinicZones')} style={{ marginBottom: 24 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('attendanceZonesHelp')}
      </Text>
      {!ownerLoading && !canManageOwnerFeatures && (
        <Alert type="info" showIcon message={t('attendanceZoneOwnerOnly')} style={{ marginBottom: 12 }} />
      )}
      {clinicsLoading || ownerLoading || zonesLoading ? (
        <Spin />
      ) : visibleClinics.length === 0 ? (
        <Text type="secondary">{t('attendanceNoClinics')}</Text>
      ) : (
        <Table rowKey="_id" dataSource={visibleClinics} columns={columns} pagination={false} scroll={{ x: true }} />
      )}
    </Card>
  );
};

export default ClinicAttendanceZoneSettings;
